import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Link,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';
import { getCustomers } from '@/features/customers/api';
import { formatPhone } from '@/features/customers/customerForm';
import { CustomerActivityChips } from '@/features/customers/components/CustomerChips';
import { NewCustomerDialog } from '@/features/customers/components/NewCustomerDialog';
import type { CustomerActivity, CustomerPage } from '@/features/customers/types';
import { formatQuotationDate } from '@/features/operations/labels';
import { StatusChip } from '@/components/StatusChip';

// Refetch delay for free-text search, so each keystroke doesn't round-trip to the API.
const SEARCH_DEBOUNCE_MS = 400;

const activityFilters: { key: keyof CustomerActivity; label: string }[] = [
  { key: 'quoted', label: 'Cotizaron' },
  { key: 'sold', label: 'Vendieron a PhoneTec' },
  { key: 'bought', label: 'Compraron' },
];

/**
 * A mail that wraps only after the `@`, never inside a word, so it can still be read out loud
 * (#229, U-3). Too long for its column even so, the table scrolls sideways.
 */
function EmailText({ email }: { email: string }) {
  const at = email.indexOf('@');

  if (at < 0) {
    return (
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {email}
      </Box>
    );
  }

  return (
    <>
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {email.slice(0, at + 1)}
      </Box>
      <wbr />
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {email.slice(at + 1)}
      </Box>
    </>
  );
}

/**
 * The customers of PhoneTec (CRM-2, #229): every account with the CUSTOMER role, searchable by
 * name, mail or phone, and filterable by what they did.
 *
 * Like the quotation list, the search and the filters live in the URL, so coming back from a
 * customer's sheet lands on the same search.
 */
export function CustomersPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const flags = {
    quoted: searchParams.get('quoted') === 'true',
    sold: searchParams.get('sold') === 'true',
    bought: searchParams.get('bought') === 'true',
  };
  const hasFilters = Boolean(search) || flags.quoted || flags.sold || flags.bought;
  const [searchInput, setSearchInput] = useState(search);
  const [result, setResult] = useState<CustomerPage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  /// Any change of search or filter goes back to the first page. Replaces the history entry:
  /// going back should leave the list, not undo a filter one click at a time.
  const setParam = (key: string, value: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);

        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }

        if (key !== 'page') {
          next.delete('page');
        }

        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed !== search) {
        setParam('search', trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // `search` and `setParam` are left out on purpose: this reacts to typing only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        setResult(
          await getCustomers(token, {
            search: search || undefined,
            ...flags,
            page,
          }),
        );
      } catch {
        setErrorMessage('No pudimos cargar los clientes. Revisá la conexión e intentá de nuevo.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
    // `flags` is rebuilt every render; its three values are the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, page, flags.quoted, flags.sold, flags.bought]);

  if (!token) {
    return null;
  }

  const customers = result?.customers ?? [];
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CLIENTES"
        title="Clientes"
        description="Buscá una cuenta para ver sus datos, corregirlos o reenviarle la invitación."
        badge="Uso interno"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <SectionCard
        title="Cuentas de cliente"
        description="Las de quienes se registraron en la web y las que se crearon en el mostrador."
      >
        <Stack spacing={2.5}>
          <Box>
            <PrimaryButton onClick={() => setIsCreating(true)}>Nueva cuenta</PrimaryButton>
          </Box>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Nombre, mail o teléfono"
                helperText="El teléfono, escrito como quieras: 099 123 456 o +598 99 123 456."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <FormLabel component="legend" sx={{ typography: 'body2', mb: 0.5 }}>
                Mostrar solo quienes
              </FormLabel>
              <FormGroup row>
                {activityFilters.map((filter) => (
                  <FormControlLabel
                    key={filter.key}
                    control={
                      <Checkbox
                        checked={flags[filter.key]}
                        onChange={(event) =>
                          setParam(filter.key, event.target.checked ? 'true' : '')
                        }
                      />
                    }
                    label={filter.label}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Se suman: si marcás dos, ves a quienes hicieron las dos cosas. Las compras aparecen
                desde que la venta registra al comprador.
              </Typography>
            </Grid>
          </Grid>

          {isLoading && !result ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}>
              <CircularProgress color="secondary" size={28} />
            </Box>
          ) : !result || errorMessage ? null : customers.length === 0 ? (
            <Alert severity="info">
              {hasFilters
                ? 'Ninguna cuenta coincide con la búsqueda o los filtros. Probá con otro dato o desmarcá algún filtro.'
                : 'Todavía no hay cuentas de cliente. Se crean cuando alguien se registra en la web, cotiza en el mostrador o con «Nueva cuenta».'}
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary" aria-live="polite">
                {result.total === 1 ? '1 cuenta' : `${result.total} cuentas`}
                {isLoading ? ' · actualizando...' : ''}
              </Typography>
              {/* Only the table scrolls sideways on a narrow screen, never the page. */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180 }}>Nombre</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Teléfono</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Mail</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Actividad</TableCell>
                      <TableCell>Alta</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => {
                      const detailPath = `/admin/customers/${customer.id}`;
                      const detailState = { from: searchParams.toString() };

                      return (
                        <TableRow
                          key={customer.id}
                          hover
                          onClick={() => navigate(detailPath, { state: detailState })}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            {/* The row is clickable with a mouse; the link is the way in from
                                the keyboard and for screen readers. */}
                            <Link
                              component={RouterLink}
                              to={detailPath}
                              state={detailState}
                              aria-label={`Abrir la ficha de ${customer.fullName}`}
                              underline="hover"
                              color="inherit"
                              fontWeight={600}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {customer.fullName}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatPhone(customer.phone)}
                          </TableCell>
                          <TableCell>
                            <EmailText email={customer.email} />
                          </TableCell>
                          <TableCell>
                            <StatusChip domain="customerAccount" status={customer.status} />
                          </TableCell>
                          <TableCell>
                            <CustomerActivityChips activity={customer.activity} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatQuotationDate(customer.createdAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              {pageCount > 1 ? (
                <Pagination
                  page={page}
                  count={pageCount}
                  onChange={(_event, value) => setParam('page', value > 1 ? String(value) : '')}
                  getItemAriaLabel={(type, value) =>
                    type === 'page'
                      ? `Ir a la página ${value}`
                      : type === 'next'
                        ? 'Página siguiente'
                        : type === 'previous'
                          ? 'Página anterior'
                          : type === 'first'
                            ? 'Primera página'
                            : 'Última página'
                  }
                />
              ) : null}
            </Stack>
          )}
        </Stack>
      </SectionCard>

      <NewCustomerDialog
        token={token}
        open={isCreating}
        onClose={() => setIsCreating(false)}
        onDone={(customer, created) =>
          navigate(`/admin/customers/${customer.id}`, {
            state: { from: searchParams.toString(), outcome: created ? 'created' : 'existing' },
          })
        }
      />
    </Stack>
  );
}
